import { useState } from "react";
import { Typography, Radio } from "@mui/material";
import { tss } from "tss-react";
import { getEntitiesBounds, getLayerSettings } from "actions/GlobalActions";
import { handleLayerEvent } from "actions/ViewerTools";
import { ViewerActions, LayersActions } from "actions";
import ViewerEvents from "events/ViewerEvents";
import RangeSliderInput from "components/RangeSliderInput";
import Section from "components/Section";
import { useStylesColorLegend } from "./ColorLegend";
import commonStyles from "styles/common";
import { deepCopyObject } from "services/UtilsService";

var defaultSliders: any = {
    x: { min: 0, max: 100, minValue: 0, maxValue: 100, step: 1 },
    y: { min: 0, max: 100, minValue: 0, maxValue: 100, step: 1 },
    z: { min: 0, max: 100, minValue: 0, maxValue: 100, step: 1 },
};

const useStyles = tss.create({
    sliders: {
        position: "relative",
        top: 10,
    },
    label: {
        width: 60,
    },
    radio: {
        width: 40,
    },
});

type blockModelLimitsType = {
    indexFilter: number;
    aabbStepMinX: number;
    aabbStepMaxX: number;
    aabbStepMinY: number;
    aabbStepMaxY: number;
    aabbStepMinZ: number;
    aabbStepMaxZ: number;
    aabbStepEnableX: boolean;
    aabbStepEnableY: boolean;
    aabbStepEnableZ: boolean;
};

let globalSettings: blockModelLimitsType[] = [];
var bounds: any, steps: any;

type IProps = {
    layer: any;
    inputs: any;
};

export default function BlockModelLimits(props: IProps) {
    const { classes, cx } = useStylesColorLegend();
    const { classes: current } = useStyles();
    const { classes: common } = commonStyles();
    var selectedLayers = LayersActions.selectedLayers;
    var { layer } = props;
    const [update, setUpdate] = useState(false);

    const isMultiple = selectedLayers.length > 1;
    if (isMultiple) {
        selectedLayers.forEach((currentLayer: any) => {
            const initialState = getLayerSettings(currentLayer, ["index_filter"], [0]);
            globalSettings[currentLayer.ID] = initialState;
        });
    } else {
        const initialState = getLayerSettings(
            layer,
            [
                "index_filter",
                "aabb_step_min_x",
                "aabb_step_max_x",
                "aabb_step_min_y",
                "aabb_step_max_y",
                "aabb_step_min_z",
                "aabb_step_max_z",
                "aabb_step_enable_x",
                "aabb_step_enable_y",
                "aabb_step_enable_z",
            ],
            [0, 0, 100, 0, 100, 0, 100, true, true, true]
        );
        globalSettings[layer.ID] = initialState;
    }
    var sliders = deepCopyObject(defaultSliders);

    var {
        indexFilter,
        aabbStepMinX,
        aabbStepMaxX,
        aabbStepMinY,
        aabbStepMaxY,
        aabbStepMinZ,
        aabbStepMaxZ,
        aabbStepEnableX,
        aabbStepEnableY,
        aabbStepEnableZ,
    } = globalSettings[layer.ID];
    const [enable, setEnable] = useState({
        x: aabbStepEnableX,
        y: aabbStepEnableY,
        z: aabbStepEnableZ,
    });

    var idsList: any = ViewerActions.getZoomToLayersIds([props.layer], props.inputs);
    bounds = getEntitiesBounds(idsList, ViewerEvents.currentProject);
    steps = ViewerActions.getStepsForLayer(props.layer.ID);
    var newBounds: any = deepCopyObject(bounds);
    var max: any = { x: 100, y: 100, z: 100 };
    if (bounds && steps) {
        max["x"] = (bounds.max.x - bounds.min.x) / steps[0];
        max["y"] = (bounds.max.y - bounds.min.y) / steps[1];
        max["z"] = (bounds.max.z - bounds.min.z) / steps[2];

        sliders["x"]["max"] = max["x"];
        sliders["y"]["max"] = max["y"];
        sliders["z"]["max"] = max["z"];

        if (typeof layer["aabb_step_min_x"] === "undefined") {
            sliders["x"]["maxValue"] = (bounds.max.x - bounds.min.x) / steps[0];
            layer["aabb_step_max_x"] = sliders["x"]["maxValue"];
        } else {
            sliders["x"]["minValue"] = aabbStepMinX;
            sliders["x"]["maxValue"] = aabbStepMaxX;
        }
        if (typeof layer["aabb_step_min_y"] === "undefined") {
            sliders["y"]["maxValue"] = (bounds.max.y - bounds.min.y) / steps[1];
            layer["aabb_step_max_y"] = sliders["y"]["maxValue"];
        } else {
            sliders["y"]["minValue"] = aabbStepMinY;
            sliders["y"]["maxValue"] = aabbStepMaxY;
        }
        if (typeof layer["aabb_step_min_z"] === "undefined") {
            sliders["z"]["maxValue"] = (bounds.max.z - bounds.min.z) / steps[2];
            layer["aabb_step_max_z"] = sliders["z"]["maxValue"];
        } else {
            sliders["z"]["minValue"] = aabbStepMinZ;
            sliders["z"]["maxValue"] = aabbStepMaxZ;
        }
        if (typeof layer["aabb_step_enable_x"] === "undefined") {
            layer["aabb_step_enable_x"] = "1";
        }
        if (typeof layer["aabb_step_enable_y"] === "undefined") {
            layer["aabb_step_enable_y"] = "1";
        }
        if (typeof layer["aabb_step_enable_z"] === "undefined") {
            layer["aabb_step_enable_z"] = "1";
        }
    }

    //console.log('bounds:', bounds);
    //console.log('steps:', steps);
    //console.log('sliders:', sliders);

    var calculateBounds = () => {
        var minAdd = {
            x: sliders["x"]["minValue"],
            y: sliders["y"]["minValue"],
            z: sliders["z"]["minValue"],
        };
        var maxAdd = {
            x: max["x"] - sliders["x"]["maxValue"],
            y: max["y"] - sliders["y"]["maxValue"],
            z: max["z"] - sliders["z"]["maxValue"],
        };
        //console.log('minAdd:', minAdd);
        //console.log('maxAdd:', maxAdd);
        const checkBoxState = getLayerSettings(
            layer,
            ["aabb_step_enable_x", "aabb_step_enable_y", "aabb_step_enable_z"],
            [true, true, true]
        );
        var { aabbStepEnableX, aabbStepEnableY, aabbStepEnableZ } = checkBoxState;
        if (!aabbStepEnableX) {
            minAdd["x"] = 0;
            maxAdd["x"] = 0;
        }
        if (!aabbStepEnableY) {
            minAdd["y"] = 0;
            maxAdd["y"] = 0;
        }
        if (!aabbStepEnableZ) {
            minAdd["z"] = 0;
            maxAdd["z"] = 0;
        }
        newBounds["min"]["x"] = bounds["min"]["x"] + minAdd["x"] * steps[0];
        newBounds["max"]["x"] = bounds["max"]["x"] - maxAdd["x"] * steps[0];
        newBounds["min"]["y"] = bounds["min"]["y"] + minAdd["y"] * steps[1];
        newBounds["max"]["y"] = bounds["max"]["y"] - maxAdd["y"] * steps[1];
        newBounds["min"]["z"] = bounds["min"]["z"] + minAdd["z"] * steps[2];
        newBounds["max"]["z"] = bounds["max"]["z"] - maxAdd["z"] * steps[2];
    };

    var changeBounds = () => {
        const checkBoxState = getLayerSettings(
            layer,
            ["aabb_step_enable_x", "aabb_step_enable_y", "aabb_step_enable_z"],
            [true, true, true]
        );
        var { aabbStepEnableX, aabbStepEnableY, aabbStepEnableZ } = checkBoxState;
        const blockModelLimits: any = {
            indexFilter: layer["index_filter"] ? parseInt(layer["index_filter"]) : 0, //0: none; 1:subset; 2:sliced
            regionType: 0, //0:box
            bounds: newBounds,
            enable: { x: aabbStepEnableX, y: aabbStepEnableY, z: aabbStepEnableZ },
        };
        //console.log('bounds:', bounds);
        //console.log('newBounds:', newBounds);
        handleLayerEvent("setBlockModelLimits", blockModelLimits, layer);
    };

    var handleIndexFilter = (evt: any) => {
        var value = parseInt(evt.target.value);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["index_filter"] = value;
            });
        } else {
            layer["index_filter"] = value;
        }
        calculateBounds();
        changeBounds();
        setUpdate(!update);
    };

    var handleChange = (name: string, value: any) => {
        //console.log('name:', name, '|value:', value);
        if (value[0] > value[1] || value[1] < value[0]) {
            return;
        }
        sliders[name]["minValue"] = value[0];
        sliders[name]["maxValue"] = value[1];

        layer["aabb_step_min_" + name] = value[0];
        layer["aabb_step_max_" + name] = value[1];
        calculateBounds();
        changeBounds();
    };

    var handleCheckbox = (name: string, value: boolean) => {
        //console.log('name:', name, '|value:', value);
        layer["aabb_step_enable_" + name] = value ? "1" : "0";
        var newEnable: any = { ...enable };
        newEnable[name] = value;
        calculateBounds();
        changeBounds();
        setEnable(newEnable);
    };

    return (
        <Section name="blockModelLimits" label="Crop Model">
            <div className={common.sectionContent}>
                <div className={classes.row}>
                    <Typography className={classes.label}>Select a cropping mode</Typography>
                </div>
                <div className={classes.row}>
                    <Radio
                        checked={indexFilter === 0}
                        onChange={handleIndexFilter}
                        value={0}
                        name="radio-index-filter"
                        size="small"
                        className={current.radio}
                    />
                    <Typography className={cx(classes.label, current.label)}>None</Typography>
                    <Radio
                        checked={indexFilter === 1}
                        onChange={handleIndexFilter}
                        value={1}
                        name="radio-index-filter"
                        size="small"
                        className={current.radio}
                    />
                    <Typography className={cx(classes.label, current.label)}>All</Typography>
                    <Radio
                        checked={indexFilter === 2}
                        onChange={handleIndexFilter}
                        value={2}
                        name="radio-index-filter"
                        size="small"
                        className={current.radio}
                    />
                    <Typography className={cx(classes.label, current.label)}>Any</Typography>
                </div>
                <div className={cx(current.sliders, indexFilter === 0 ? common.disabledEvents : "")}>
                    <RangeSliderInput
                        name="x"
                        title="X"
                        min={sliders.x.min}
                        max={sliders.x.max}
                        step={sliders.x.step}
                        enable={enable["x"]}
                        value={[sliders.x.minValue, sliders.x.maxValue]}
                        handleChange={handleChange}
                        handleCheckbox={handleCheckbox}
                    />
                    <RangeSliderInput
                        name="y"
                        title="Y"
                        min={sliders.y.min}
                        max={sliders.y.max}
                        step={sliders.y.step}
                        enable={enable["y"]}
                        value={[sliders.y.minValue, sliders.y.maxValue]}
                        handleChange={handleChange}
                        handleCheckbox={handleCheckbox}
                    />
                    <RangeSliderInput
                        name="z"
                        title="Z"
                        min={sliders.z.min}
                        max={sliders.z.max}
                        step={sliders.z.step}
                        enable={enable["z"]}
                        value={[sliders.z.minValue, sliders.z.maxValue]}
                        handleChange={handleChange}
                        handleCheckbox={handleCheckbox}
                    />
                </div>
            </div>
        </Section>
    );
}
